import { useState } from 'react'
import {
  Anchor,
  Chip,
  Button,
  Spinner,
  Modal,
  Grid,
  Tooltip,
} from '@enterprise-ui/canvas-ui-react'
import EnterpriseIcon, {
  CheckCircleIcon,
  HelpIcon,
  DownloadIcon,
  CautionIcon,
  InfoIcon,
} from '@enterprise-ui/icons'
import isNumber from 'lodash/isNumber'
import { useUploadHistory } from './useUploadHistory'
import { BULK_ALERT } from '../../../constants/alertConstants'
import { useFirefly } from '../../../utilities/useFirefly'
import { FIREFLY_EVENTS } from '../../../constants/fireFlyConstants'
import { useMain } from '../../main/useMain'

export const MaintainLinkCellRenderer = (props) => {
  return <Anchor href="#">{props.value}</Anchor>
}
export const DownloadCellRenderer = (props) => {
  const { downloadFailure, downloadButtonState } = useUploadHistory()
  const { data } = props
  const { request_status, num_units_failure } = data
  // show download only when failure is greater than 0
  if (num_units_failure > 0) {
    // show download with this status
    switch (request_status) {
      case 'PROCESSING_COMPLETED':
      case 'FAILED':
        return (
          <Button
            type="ghost"
            onClick={(eve) => downloadFailure(eve, data)}
            aria-label={`Download rejected items of ${data.request_file_name} in .xlsx format`}
          >
            {downloadButtonState.inProgress ? (
              <>
                {
                  <Spinner
                    id="spinner-loading-file-download"
                    data-testid="spinner-loading-file-download"
                    size="dense"
                    className="hc-mr-min"
                  />
                }{' '}
                {BULK_ALERT.BLK_EXPORT_IN_PROGRESS_BUTTON}
              </>
            ) : (
              <>
                <EnterpriseIcon icon={DownloadIcon} className="hc-mr-min" />
                Download rejected items
              </>
            )}
          </Button>
        )
      case 'UPLOADED':
      case 'INITIATED':
      case 'PROCESSING':
      case 'Warning':
        return <></>
      default:
        return <></>
    }
  } else {
    return <></>
  }
}
export const FileStatusRenderer = (props) => {
  const { handleTrackEvent } = useFirefly()
  const { getUserProfile } = useMain()
  const [isOpen, setIsOpen] = useState(false)

  const { data, value } = props
  const tooltip = data.reason
  const { request_file_name, uploaded_on, id } = data

  switch (value) {
    case 'PROCESSING_COMPLETED':
      return 'Processed'
    case 'FAILED':
      return (
        <>
          <span className="hc-clr-error">Processing failed</span>
          {tooltip && (
            <>
              <Button
                type="ghost"
                onClick={() => {
                  setIsOpen((p) => !p)
                  // To track the click event for the help icon
                  const eventData = {
                    user: {
                      id: getUserProfile?.lan_id,
                      customdimension1: getUserProfile?.email_id,
                      customdimension2: getUserProfile?.user_info,
                    },
                    view: {
                      name: 'Bulk Upload',
                      type: 'page',
                    },
                    event: {
                      type: FIREFLY_EVENTS.EVENT_TYPE
                        .UPLOAD_HISTORY_GRID_ACTIONS,
                    },
                    customInteraction: {
                      location: 'Bulk Upload',
                      key: FIREFLY_EVENTS.CUSTOM_INTERACTION_KEYS
                        .HELPER_TEXT_CLICK,
                      value: JSON.stringify({
                        requestId: id,
                        reason: tooltip,
                      }),
                    },
                  }
                  handleTrackEvent(eventData)
                }}
                className="hc-mb-min icon-button"
                aria-label="Opens modal to view failure reason"
              >
                <Tooltip
                  content="View Details"
                  location="top"
                  aria-label={`View Details of request file name ${request_file_name} which was uploaded on ${uploaded_on}`}
                >
                  <EnterpriseIcon
                    icon={InfoIcon}
                    size={'sm'}
                    style={{ position: 'relative', bottom: '6px' }}
                  />
                </Tooltip>
              </Button>
              <Modal
                isVisible={isOpen}
                headingText="File Processing Error Details"
                onRefuse={() => setIsOpen(false)}
                size="dense"
              >
                <div className="hc-pa-normal">
                  <Grid.Container className="hc-pb-normal">
                    <Grid.Item xs={12}>
                      <strong>Request ID:</strong> {id} <br />
                      <strong>Filename:</strong> {request_file_name}
                      <br />
                      <strong>Uploaded on:</strong> {uploaded_on}
                      <br />
                      <strong>File Status:</strong> Processing failed
                      <br />
                    </Grid.Item>
                    <Grid.Item xs>
                      <p>{tooltip}</p>
                    </Grid.Item>
                  </Grid.Container>
                </div>
              </Modal>
            </>
          )}
        </>
      )
    case 'UPLOADED':
    case 'INITIATED':
    case 'PROCESSING':
      return 'Processing'
    case 'Warning':
      return <Chip icon={<HelpIcon />} color="warning" label="Warning" />
    default:
      return <></>
  }
}

export const ValidationStatus = (props) => {
  const { data, value } = props
  const { num_units_success, num_units_failure } = data
  const approved = isNumber(num_units_success) ? num_units_success : null
  const rejected = isNumber(num_units_failure) ? num_units_failure : null
  switch (value) {
    case 'PROCESSING_COMPLETED':
    case 'UPLOADED':
    case 'INITIATED':
      return (
        <div data-testid="validation-status">
          {approved !== null && (
            <Chip size="dense" className="hc-clr-success success-chip">
              <EnterpriseIcon icon={CheckCircleIcon} aria-hidden="true" />{' '}
              {approved.toLocaleString()} Approved
            </Chip>
          )}
          {rejected !== null && (
            <Chip size="dense" className="hc-clr-error error-chip">
              <EnterpriseIcon icon={CautionIcon} aria-hidden="true" />{' '}
              {rejected.toLocaleString()} Rejected
            </Chip>
          )}
        </div>
      )
    case 'PROCESSING':
    case 'FAILED':
    case 'Warning':
      return <></>
    default:
      return <></>
  }
}
